import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MAX_ROWS_IN_DOM, NEW_ROWS_POLL_MS, PAGE_SIZE } from "../constants";
import worklistApi from "../services/worklistApi";
import {
  CountResult,
  Row,
  SortState,
  WorklistFilter,
  WorklistRequest,
} from "../types";
import { isAbort, rowKey } from "../utils";

export function useWorklist(
  tabId: number | null,
  filters: WorklistFilter[],
  sort: SortState | null,
  enabled = true
) {
  const [rows, setRows] = useState<Row[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [pageNo, setPageNo] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState<CountResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newRowsAvailable, setNewRowsAvailable] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const loadingRef = useRef(false);

  const baseRequest = useMemo<WorklistRequest | null>(
    () =>
      tabId === null
        ? null
        : {
            // Same body for Page, Count and ExportCsv; load-more overrides cursor/pageNumber.
            tabId,
            pageSize: PAGE_SIZE,
            cursor: null,
            pageNumber: 1,
            sortColumn: sort?.key ?? "",
            sortDirection: sort?.dir ?? "desc",
            filters,
          },
    [tabId, filters, sort]
  );

  // First page + count in parallel; the grid never waits for the count.
  useEffect(() => {
    if (!enabled || !baseRequest) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setRows([]);
    setCursor(null);
    setPageNo(1);
    setHasMore(false);
    setCount(null);
    setError(null);
    setNewRowsAvailable(false);
    setLoading(true);
    loadingRef.current = true;

    worklistApi
      .page(baseRequest, ac.signal)
      .then((p) => {
        setRows(p.rows);
        setCursor(p.nextCursor);
        setHasMore(p.hasMore);
      })
      .catch((e) => {
        if (!isAbort(e)) setError("Could not load requisitions.");
      })
      .finally(() => {
        if (!ac.signal.aborted) {
          setLoading(false);
          loadingRef.current = false;
        }
      });

    worklistApi
      .count(baseRequest, ac.signal)
      .then(setCount)
      .catch(() => undefined); // count is optional
    return () => ac.abort();
  }, [baseRequest, enabled, reloadToken]);

  const loadMore = useCallback(async () => {
    if (
      !baseRequest ||
      loadingRef.current ||
      !hasMore ||
      rows.length >= MAX_ROWS_IN_DOM
    )
      return;
    const ac = abortRef.current ?? new AbortController();
    loadingRef.current = true;
    setLoading(true);
    try {
      // Default sort -> keyset cursor; custom sort -> PageNumber (server falls back to OFFSET).
      const req: WorklistRequest = sort
        ? { ...baseRequest, pageNumber: pageNo + 1 }
        : { ...baseRequest, cursor };
      const p = await worklistApi.page(req, ac.signal);
      setRows((r) => [...r, ...p.rows]);
      setCursor(p.nextCursor);
      setHasMore(p.hasMore);
      setPageNo((n) => n + 1);
    } catch (e) {
      if (!isAbort(e)) setError("Could not load more rows.");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [baseRequest, hasMore, rows.length, sort, cursor, pageNo]);

  // "New requisitions" check: one keyset row (PageSize 1) every 60 s, default sort only.
  const topKey = rows.length ? rowKey(rows[0]) : 0;
  useEffect(() => {
    if (!enabled || !baseRequest || sort || !topKey) return;
    const timer = setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const p = await worklistApi.page({ ...baseRequest, pageSize: 1 });
        if (p.rows.length && rowKey(p.rows[0]) > topKey)
          setNewRowsAvailable(true);
      } catch {
        // ignore
      }
    }, NEW_ROWS_POLL_MS);
    return () => clearInterval(timer);
  }, [baseRequest, sort, topKey, enabled]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  return {
    rows,
    hasMore,
    loading,
    count,
    error,
    loadMore,
    reload,
    newRowsAvailable,
    capped: rows.length >= MAX_ROWS_IN_DOM,
    currentRequest: baseRequest,
  };
}
